// The dashboard's boot context, read from the iframe URL (HOST_APP_CONVENTIONS.md §3.1: "boot
// context arrives only through the URL"). Plain JavaScript: no Vue, no store, no refs — the URL
// cannot change while the page lives (the host recreates the iframe with a fresh `src` instead,
// §3.3), so a parsed snapshot is the whole truth.
//
// Semantics are project-overview's `parseQuerystring` as it was before this package existed
// (App/setup.js up to 6b-1), kept EXACTLY for the five values it used:
//   - values are `URLSearchParams.get()` results: percent-decoded, `+` → space, `null` when the
//     param is absent (never `undefined`, never trimmed);
//   - "missing" means falsy: absent OR empty (`locale=` is missing);
//   - `locale` is the RAW param. Normalisation (`normalizeLocale` from `@rdrive/i18n`) happens at
//     one write site — the dashboard's `setValues` (./dashboardContext.js, 6b-2b) — and must not be
//     repeated here;
//   - the user id is the token's `rdrive_user_id` claim via `decodeJwtPayload` from
//     `@rdrive/helper-host-bridge` — `undefined` when the token is absent, malformed (that helper
//     then logs one `console.warn` and returns null) or carries no claim.
// Added per HOST_APP_CONVENTIONS §3.1: `theme`, the host's `userId` param, `hostOrigin`,
// `tokenScope`.
//
// USER-ID POLICY (6b-2a): the token's claim is AUTHORITATIVE. The host's `$myUserId` is believed,
// not confirmed, to be the same id — and it feeds the "assigned to me" SQL — so the URL param is
// only a fallback for a token with no claim. When both exist and differ, the claim wins and ONE
// `console.warn` says so (lengths only: never a token, never an id). `userIdParam` and
// `userIdClaim` expose the two sources separately.

import { decodeJwtPayload } from '@rdrive/helper-host-bridge';

/** The params every rdrive dashboard needs to boot — the default `required` list. */
export const PLATFORM_REQUIRED_PARAMS = Object.freeze([
  'scopedToken',
  'projectName',
  'projectCode',
  'locale',
  'apiUrl',
]);

const THEMES = ['light', 'dark'];

/**
 * @typedef {Object} PlatformParams
 * @property {string|null} scopedToken  the host-minted token (`$scopedToken`)
 * @property {string|null} projectName  `$projectName`
 * @property {string|null} projectCode  `$projectCode`
 * @property {string|null} locale       RAW `$myUserLocale` — the dashboard's setValues normalises it
 * @property {string|null} apiUrl       `$apiURL`: an API host only (`uk.rdrive.io`), no scheme
 * @property {string|null} tokenScope   `$tokenScope` (`'fragment read'` / `'fragment read write'`)
 * @property {'light'|'dark'|null} theme the host's `$theme` (its OS colour scheme) when it is
 *   exactly `light` or `dark`; otherwise null and the caller keeps its own fallback
 * @property {*} userId  THE user id: the token's `rdrive_user_id` claim when it has one (non-empty);
 *   else the `userId` param when non-empty; else the claim as decoded (`undefined` without one).
 *   A claim and a param that differ → the claim, plus one `console.warn`. Resolved on FIRST READ
 *   (that is when the token is decoded and the warning, if any, is logged), then memoised
 * @property {string|null} userIdParam  the RAW `userId` param (`$myUserId`): `null` when absent
 * @property {*} userIdClaim  the token's `rdrive_user_id` claim as decoded — `undefined` when the
 *   token is absent, malformed or has no claim. A getter: the token is decoded on first read of
 *   this or of `userId`, once
 * @property {string|null} hostOrigin   the RAW `hostOrigin` param (a literal the admin types into
 *   the iframe URL). Not resolved: `resolveHostOrigin()` from `@rdrive/helper-host-bridge`
 *   stays the one resolution (param → referrer → ancestorOrigins)
 * @property {string} queryString       the whole `?…` string as parsed (the welcome dialog's
 *   dev-mode copy)
 * @property {string[]} missing         the `required` names whose value is falsy, in `required`
 *   order; empty when the boot context is complete
 */

/**
 * Parse the boot context ONCE per call.
 *
 * @param {Object} [options]
 * @param {string[]} [options.required=PLATFORM_REQUIRED_PARAMS] the params that must be present
 *   (non-empty) — any URL param name, not only the fields above
 * @param {string} [options.search=window.location.search] the query string to parse (tests, SSR)
 * @returns {PlatformParams} a plain object — NOT reactive, NOT frozen
 */
export function usePlatformParams({ required = PLATFORM_REQUIRED_PARAMS, search } = {}) {
  const queryString =
    search !== undefined ? search : typeof window !== 'undefined' ? window.location.search : '';
  const urlParams = new URLSearchParams(queryString);

  const scopedToken = urlParams.get('scopedToken');
  const themeParam = urlParams.get('theme');
  const userIdParam = urlParams.get('userId');

  let claimDecoded = false;
  let claim;
  const readClaim = () => {
    if (!claimDecoded) {
      claimDecoded = true;
      claim = decodeJwtPayload(scopedToken)?.rdrive_user_id;
    }
    return claim;
  };

  let userIdResolved = false;
  let userId;
  const resolveUserId = () => {
    const tokenClaim = readClaim();
    const hasClaim = tokenClaim !== undefined && tokenClaim !== null && tokenClaim !== '';
    if (hasClaim) {
      if (userIdParam && String(tokenClaim) !== userIdParam) {
        console.warn(
          `[rdrive] The userId URL param does not match the scoped token's rdrive_user_id claim; ` +
            `using the claim (param ${userIdParam.length} chars, claim ${String(tokenClaim).length} chars).`
        );
      }
      return tokenClaim;
    }
    // No claim: the host's param, else whatever the token gave (undefined/null/'' — as before).
    return userIdParam || tokenClaim;
  };

  return {
    scopedToken,
    projectName: urlParams.get('projectName'),
    projectCode: urlParams.get('projectCode'),
    locale: urlParams.get('locale'),
    apiUrl: urlParams.get('apiUrl'),
    tokenScope: urlParams.get('tokenScope'),
    theme: THEMES.includes(themeParam) ? themeParam : null,
    get userId() {
      if (!userIdResolved) {
        userIdResolved = true;
        userId = resolveUserId();
      }
      return userId;
    },
    userIdParam,
    get userIdClaim() {
      return readClaim();
    },
    hostOrigin: urlParams.get('hostOrigin'),
    queryString,
    missing: required.filter((name) => !urlParams.get(name)),
  };
}
